// Prepara o endereço de uma UC para consulta em geocoder. Existe porque o
// `logradouro` da UC guarda a linha CRUA da fatura da Equatorial — "RUA C-217,
// Q. 515, L. 14, S/N, CASA - 2" — e mandar isso como nome de rua não acha nada:
// medido em 11 endereços reais de clientes, o Nominatim acertou 0; com só o nome
// da rua, acertou 10. Era essa a causa de 125 dos 178 clientes ganhos estarem
// plotados no centro do município em vez do endereço deles.
//
// A separação em si é a de `endereco-goias.ts` (já usada pelo extrair-fatura-crm
// e já testada). Aqui em cima dela vão só as decisões que são do geocoder:
// o que é rua buscável, o que é número de porta de verdade e como comparar
// nomes de cidade que voltam acentuados de um lado e não do outro.

import { splitEnderecoGoias } from './endereco-goias.ts';

export interface EnderecoUC {
  logradouro?: string | null;
  numero?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
  cep?: string | null;
}

export interface ConsultaGeocode {
  /** Nome da rua isolado. Vazio = não há por onde buscar por logradouro. */
  rua: string;
  /** Número da porta. Vazio quando é S/N, 0 ou não veio — S/N como número derruba a busca. */
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
}

/** "Segunda via", "2ª VIA" — cabeçalho da fatura que o OCR gruda no endereço. */
const RE_SEGUNDA_VIA = /^\s*(?:2\s*[ªa]?\s*|segunda\s+)via\b[\s,;.\-]*/i;
/** Rua sem nome de verdade: a Equatorial usa isso em zona rural. Não é buscável. */
const RE_SEM_NOME = /^\s*(?:RUA|AV|AVENIDA)?\s*SEM\s+NOME\b/i;
/** Número de porta utilizável: só dígitos, com sufixo de letra opcional (12A). */
const RE_NUMERO_UTIL = /^\d+[A-Za-z]?$/;
/**
 * Começo de nome de logradouro. Serve para escolher o segmento certo quando o
 * `splitEnderecoGoias` devolve mais de um: ele só corta no que reconhece como
 * estrutura (Q./L./número/complemento) e devolve o resto colado — "Segunda via,
 * AVENIDA STA MARIA, Q. GLEBA 2A" volta inteiro porque "Q. GLEBA 2A" tem espaço
 * no meio e escapa do regex de quadra de lá. Para o geocoder o que vale é a
 * avenida; o resto é ruído que faz a busca não achar.
 */
const RE_TIPO_LOGRADOURO =
  /^(?:R|RUA|AV|AVE|AVN|AVENIDA|AL|ALAMEDA|TV|TRAV|TRAVESSA|PC|PCA|PRACA|PRAÇA|ROD|RODOVIA|EST|ESTRADA|VIELA|LARGO|MARGINAL|VIA|LADEIRA|PARQUE)[\s.]/i;

/** Caixa alta, sem acento, espaço colapsado. Para comparar cidade, não para exibir. */
export function normalizarCidade(valor?: string | null): string {
  return (valor || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Compara dois nomes de lugar ignorando acento e caixa — o geocoder devolve
 * "Itaberaí" e o cadastro guarda "ITABERAI". Ausência nunca casa: sem nome dos
 * dois lados não há confirmação nenhuma, e tratar isso como acerto reintroduz
 * exatamente o palpite silencioso que a checagem existe para barrar.
 */
function mesmoLugar(a?: string | null, b?: string | null): boolean {
  const na = normalizarCidade(a);
  const nb = normalizarCidade(b);
  return na !== '' && nb !== '' && na === nb;
}

/**
 * A cidade que o geocoder entendeu bate com a da UC? É o que impede aceitar um
 * palpite de outro município: caso real medido, uma UC de Itaberaí resolvida em
 * Iporá, 148 km fora, com o resto da resposta plausível.
 */
export const mesmaCidade = mesmoLugar;

/**
 * O nome da cidade sem a UF grudada no fim — "Aparecida de Goiania Go" vira
 * "APARECIDA DE GOIANIA".
 *
 * O campo `cidade` do cadastro é texto livre e às vezes veio com o estado
 * dentro. Comparado cru contra o "Aparecida de Goiânia" que o geocoder devolve,
 * ele acusa cidade divergente e a resposta boa é descartada: a UC 1761 ficou
 * assim, com a coordenada jogada fora por causa de duas letras a mais.
 *
 * Só sai o que é sigla de estado de verdade, e só no fim: nenhum município
 * brasileiro termina numa palavra de duas letras que seja UF.
 */
const UFS = new Set([
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]);

export function cidadeSemUf(valor?: string | null): string {
  const partes = normalizarCidade(valor).replace(/[.,\/-]+/g, ' ').split(/\s+/).filter(Boolean);
  if (partes.length > 1 && UFS.has(partes[partes.length - 1])) partes.pop();
  return partes.join(' ');
}

/**
 * O bairro bate? Usado para desempatar rua de nome genérico ("RUA 02" se repete
 * dezenas de vezes numa cidade). Bairro divergente não invalida a resposta —
 * ruas longas atravessam vários e o cadastro às vezes traz nome de condomínio —,
 * só autoriza procurar uma alternativa melhor.
 */
export const mesmoBairro = mesmoLugar;

/** Tipo de logradouro no começo do nome. Sai antes de comparar: o cadastro diz
 *  "AVENIDA" onde o OSM diz "Av." e isso não é diferença de rua. */
const RE_PREFIXO_TIPO =
  /^(?:R|RUA|AV|AVE|AVN|AVENIDA|AL|ALA|ALAMEDA|TV|TRAV|TRAVESSA|PC|PCA|PRACA|ROD|RODOVIA|EST|ESTRADA|VIELA|LARGO|MARGINAL|VIA|LADEIRA|PARQUE)\s+/;
/** Partículas que o cadastro engole e o OSM escreve: "Couto Magalhães" x "Couto de Magalhães". */
const RE_PARTICULA = /\b(?:DE|DA|DO|DAS|DOS|E)\b/g;

/**
 * Forma canônica de um nome de logradouro para COMPARAÇÃO (nunca para busca nem
 * para exibir). Tira acento, tipo, pontuação, partículas e zero à esquerda de
 * número, que é onde moram as diferenças cosméticas entre o cadastro da
 * Equatorial e o OSM: "RUA CP-10" x "Rua CP 10", "RUA BARAO MAUA" x "Rua Barão
 * de Mauá", "RUA 02" x "Rua 2".
 */
export function normalizarLogradouro(valor?: string | null): string {
  return normalizarCidade(valor)
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
    .replace(RE_PREFIXO_TIPO, '')
    .replace(RE_PARTICULA, ' ')
    .split(/\s+/)
    .filter(Boolean)
    // Zero à esquerda em número de rua é cosmético: "RUA 02" e "Rua 2" são a mesma.
    .map((t) => (/^\d+$/.test(t) ? String(Number(t)) : t))
    .join(' ');
}

/**
 * A rua que o geocoder devolveu é a que foi pedida?
 *
 * A busca estruturada do Nominatim é tolerante: quando a rua pedida não existe
 * na base, ela devolve OUTRA rua da mesma cidade em vez de nada — e a checagem
 * de município deixa passar, porque a cidade está certa. Foi assim que um
 * cliente da Avenida Professor Edvan foi parar na Rua Marechal Rondon. Sem esta
 * conferência, o degrau 'endereco' promete precisão de rua que não tem.
 */
export function mesmaRua(a?: string | null, b?: string | null): boolean {
  const na = normalizarLogradouro(a);
  const nb = normalizarLogradouro(b);
  return na !== '' && nb !== '' && na === nb;
}

export function prepararConsulta(uc: EnderecoUC): ConsultaGeocode {
  const split = splitEnderecoGoias(uc.logradouro);

  const segmentos = split.logradouro
    .replace(RE_SEGUNDA_VIA, '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  // Primeiro segmento que parece logradouro; na falta de qualquer um, o primeiro
  // (endereço sem tipo, tipo "SETOR CENTRAL", ainda é o melhor palpite que há).
  let rua = segmentos.find((s) => RE_TIPO_LOGRADOURO.test(s)) || segmentos[0] || '';
  // Rua de 3 caracteres ou menos é sobra de OCR, não logradouro.
  if (RE_SEM_NOME.test(rua) || rua.length <= 3) rua = '';

  // A coluna `numero` da UC vem vazia ou 'S/N' em 213 das 284 UCs de clientes
  // ganhos — o número real está dentro do logradouro. A coluna tem prioridade
  // quando é utilizável; senão vale o que o split achou.
  const candidatos = [uc.numero, split.numero];
  let numero = '';
  for (const c of candidatos) {
    const v = String(c || '').trim();
    if (RE_NUMERO_UTIL.test(v) && Number(v.replace(/\D/g, '')) > 0) { numero = v; break; }
  }

  return {
    rua,
    numero,
    bairro: (uc.bairro || '').trim(),
    cidade: (uc.cidade || '').trim(),
    estado: (uc.estado || '').trim(),
  };
}
